import classNames from "classnames";
import {
  BUBBLE_POSITION_STORAGE_KEY,
  PROPILOT_BUBBLE_SIZE,
  PROPILOT_BUBBLE_Z_INDEX,
  PROPILOT_DEFAULT_POSITION,
  PROPILOT_PANEL_HEIGHT,
  PROPILOT_PANEL_MIN_HEIGHT,
  PROPILOT_PANEL_MIN_WIDTH,
  PROPILOT_PANEL_WIDTH,
  PROPILOT_PANEL_Z_INDEX
} from "PFApp/components/propilot/constants";
import { MessageRenderer } from "PFApp/components/propilot/core/message_renderer";
import { ProPilotBubble } from "PFApp/components/propilot/core/propilot_bubble/propilot_bubble";
import { ProPilotConversation } from "PFApp/components/propilot/core/propilot_conversation";
import { ProPilotPanelHeader } from "PFApp/components/propilot/core/propilot_panel_header";
import { useProPilotConversation } from "PFApp/components/propilot/hooks/use_propilot_conversation";
import { type ProPilotStatus, useProPilotLifecycleActions } from "PFApp/stores/propilot_lifecycle_store";
import { Stack } from "PFComponents/containers";
import type { DropZone } from "PFComponents/floating_expandable_bubble";
import { FloatingExpandableBubble } from "PFComponents/floating_expandable_bubble";
import {
  useFloatingEntry,
  useFloatingPositionStoreActions
} from "PFComponents/floating_expandable_bubble/floating_position_store";
import { useId } from "react";
import { createPortal } from "react-dom";

import css from "./propilot_session.module.scss";

type ProPilotSessionProps = {
  status: ProPilotStatus;
  container: HTMLElement;
  dropZones: DropZone[];
  onDragOverZone: (zoneId: string | null) => void;
  onDropInZone: (zoneId: string) => void;
  onDragStateChange: (isDragging: boolean) => void;
};

export const ProPilotSession = ({
  status,
  container,
  dropZones,
  onDragOverZone,
  onDropInZone,
  onDragStateChange
}: ProPilotSessionProps) => {
  const titleId = useId();
  const { setOpen, setPosition } = useFloatingPositionStoreActions();
  const { isOpen } = useFloatingEntry(BUBBLE_POSITION_STORAGE_KEY);
  const { turnOff } = useProPilotLifecycleActions();

  const {
    conversationId,
    messages,
    status: conversationStatus,
    allowFreeText,
    isConnected,
    pendingRetry,
    submit,
    submitOptionReply,
    retry,
    clearConversation
  } = useProPilotConversation();

  const handleTurnOff = () => {
    setOpen(BUBBLE_POSITION_STORAGE_KEY, false);
    setPosition(BUBBLE_POSITION_STORAGE_KEY, PROPILOT_DEFAULT_POSITION);
    turnOff();
  };

  const showFloatingElement = status === "floating" || (status === "docked" && isOpen);
  if (!showFloatingElement) {
    return null;
  }

  return createPortal(
    <FloatingExpandableBubble
      storageKey={BUBBLE_POSITION_STORAGE_KEY}
      bubbleSize={PROPILOT_BUBBLE_SIZE}
      triggerZIndex={PROPILOT_BUBBLE_Z_INDEX}
      panelZIndex={PROPILOT_PANEL_Z_INDEX}
      panelSize={{ width: PROPILOT_PANEL_WIDTH, height: PROPILOT_PANEL_HEIGHT }}
      minPanelSize={{ width: PROPILOT_PANEL_MIN_WIDTH, height: PROPILOT_PANEL_MIN_HEIGHT }}
      resizable
      dropZones={dropZones}
      onDragOverZone={onDragOverZone}
      onDropInZone={onDropInZone}
      onDragStateChange={onDragStateChange}
      renderCollapsed={({ panelId, triggerRef, onToggle }) => (
        <ProPilotBubble ref={triggerRef} isOpen={false} panelId={panelId} onClick={onToggle} />
      )}
      renderExpanded={({ id, dragHandleProps, initialFocusRef, onClose, areHeaderControlsVisible }) => {
        const inputFocusRef = allowFreeText ? initialFocusRef : undefined;
        const minimizeFocusRef = allowFreeText ? undefined : initialFocusRef;

        return (
          <Stack
            id={id}
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            className={classNames(css.panel, { [css.panelOpen]: isOpen })}
            qaId="ProPilotPanel"
          >
            <ProPilotPanelHeader
              titleId={titleId}
              dragHandleProps={dragHandleProps}
              areHeaderControlsVisible={areHeaderControlsVisible}
              minimizeRef={minimizeFocusRef}
              onClear={clearConversation}
              onMinimize={onClose}
              onTurnOff={handleTurnOff}
            />
            <ProPilotConversation
              conversationId={conversationId}
              messages={messages}
              status={conversationStatus}
              allowFreeText={allowFreeText}
              isConnected={isConnected}
              onSubmit={submit}
              inputRef={inputFocusRef}
              renderMessage={(msg) => (
                <MessageRenderer
                  message={msg.content}
                  author={msg.author}
                  onOptionSelected={submitOptionReply}
                  onRetry={pendingRetry ? retry : undefined}
                />
              )}
            />
          </Stack>
        );
      }}
    />,
    container
  );
};
